import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Box,
  CssBaseline,
  Container,
  Menu,
  MenuItem,
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme, lightTheme } from './themes';
import About from './pages/about';
import Home from './pages/home';
import Footer from './pages/footer';
import ContactUs from './pages/contactUs';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import RegistrationPage from './pages/registration';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import Cookies from './pages/cookies';
import Terms from './pages/terms';
import LoginPage from './pages/authentication/login';
import PasswordResetPage from './pages/authentication/forgot-password';
import PrivacyPolicy from './pages/policies/privacy_policy';
import UserDashboard from './pages/dashboard/users';
import AdminDashboard from './pages/dashboard/admin';
import { Link as RouterLink } from 'react-router-dom';
import { getAuthToken, logout } from './network/auth';
import useAuthStore from './store/authstore';
import { isAdmin } from './utilities/user';
import { jwtDecode } from 'jwt-decode';
import PackagesSection from './pages/packages';
import Settings from './pages/settings';

const App = () => {
  //const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { user } = useAuthStore();

  useEffect(() => {
    // If token available in the local storage, then update the store.
    const idToken = getAuthToken();

    if (idToken) {
      useAuthStore.getState().loggedin(jwtDecode(idToken));
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // const handleMenuOpen = (event) => {
  //   setAnchorEl(event.currentTarget);
  // };

  // const handleMenuClose = () => {
  //   setAnchorEl(null);
  // };

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Router>
        {/* Main AppBar */}
        <AppBar
          position="static"
          sx={{
            p: 2,
            pr: 2,
            pl: 2,
            background: (theme) => theme.palette.container.main,
            //background: (theme) => `linear-gradient(90deg, ${theme.palette.container.main} 10%, ${theme.palette.container.dark} 100%)`,
            backgroundColor: (theme) => theme.palette.container.main,
          }}
        >
          <Toolbar>
            {/* <IconButton edge="start" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton> */}
            <AppRegistrationIcon sx={{ mr: 2, fontSize: 64 }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                UK Company
              </Typography>
              <Typography variant="h4">Incorportation</Typography>
              {/* <Typography variant="h5">UK</Typography> */}
            </Typography>

            {/* Tab Section */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="inherit"
            >
              <Tab label="Home" component={Link} to="/" />
              {user !== null && isAdmin(user) && (
                <Tab
                  label="Dashboard"
                  component={Link}
                  to={'/dashboard/admin'}
                />
              )}
              {user !== null && !isAdmin(user) && (
                <Tab
                  label="Dashboard"
                  component={Link}
                  to={'/dashboard/user'}
                />
              )}
              <Tab label="About" component={Link} to="/about" />
              <Tab label="Contact" component={Link} to="/contact" />
            </Tabs>

            {/* Login/Account Button */}
            <Box sx={{ ml: 2 }}>
              {/* <IconButton component={Link} to="/login" color="inherit"> */}
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="account menu"
                aria-controls="user-menu"
                aria-haspopup="true"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <AccountCircle />
              </IconButton>
              {/* ToDo: Below Menu item and assoicated code to be removed.*/}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {user === null && (
                  <MenuItem
                    component={RouterLink}
                    to="/login"
                    onClick={() => setAnchorEl(null)}
                  >
                    Login
                  </MenuItem>
                )}
                {user !== null && (
                  <>
                    <MenuItem
                      disabled
                      sx={{ opacity: 1, color: 'text.primary' }}
                    >
                      <Typography variant="body2">{user.name}</Typography>
                    </MenuItem>
                    <MenuItem
                      component={RouterLink}
                      onClick={async () => {
                        await logout();
                        //navigate("/login", { replace: true });
                        setAnchorEl(null);
                      }}
                    >
                      Logout
                    </MenuItem>
                  </>
                )}
              </Menu>
            </Box>

            {/* Dark Mode Toggle */}
            <Box sx={{ ml: 2 }}>
              <IconButton onClick={toggleDarkMode} color="inherit">
                {darkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem component={Link} to="/" onClick={handleMenuClose}>
            Home
          </MenuItem>
          <MenuItem component={Link} to="/about" onClick={handleMenuClose}>
            About
          </MenuItem>
        </Menu> */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/about"
            element={
              <Container pt={2}>
                <About />
              </Container>
            }
          />
          <Route
            path="/packages"
            element={
              <Container pt={2}>
                <PackagesSection />
              </Container>
            }
          />
          <Route
            path="/settings"
            element={
              <Container pt={2}>
                <Settings />
              </Container>
            }
          />
          <Route
            path="/contact"
            element={
              <Container pt={2}>
                <ContactUs />
              </Container>
            }
          />
          <Route path="/register/:planId" element={<RegistrationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard/user" element={<UserDashboard />} />
          <Route path="/dashboard/admin" element={<AdminDashboard />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </Router>
      <ToastContainer />
      <Footer />
    </ThemeProvider>
  );
};

export default App;
